import React, {
  MouseEvent,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { StartOfWeekFormatSetting, TimeFormatSetting, TimeslotDay } from '../../../../models/graphql';
import { Calendar } from '../../../../components/containers/Calendar';
import { PostingPlanHelpers } from '../postingPlanHelpers';
import { useSettings } from '../../../../hooks/useSettings';
import { PostingPlanContext } from '../../../../providers/PostingPlanProvider';
import { useCursorAttachment } from '../../../../hooks/useCursorAttachment';
import { BreakpointsEnum } from '../../../../constants/breakpoints';
import { useBreakpoints } from '../../../../hooks/useBreakpoints';
import { CloneCellTemplate } from '../CloneCellTemplate';
import classes from '../PostingPlan.module.scss';
import calendarClasses from './PostingPlanCloneCalendar.module.scss';
import classNames from 'classnames';
import { useDimensions } from '../../../../hooks/useDimensions';
import { useDidMount } from '../../../../hooks/useDidMount';
import { Timeslot } from '../Timeslot';
import { profilesHelpers } from '../../../Settings/Profiles/Profiles.helpers';
import { TimeslotSocialProfileType } from '../Timeslot/Timeslot.view';
import { useTableCellTimeTracking } from '../../../../hooks/useTableCellTimeTracking';
import { addMinutes, format } from 'date-fns';
import { GetTimeslots_workspace_timeslots } from '../../../../models/legacy-graphql';
import { TopNavContext } from '../../../../providers/TopNavProvider';
import { topNavOffsetClassesGetter } from '../../../../helpers/topNavOffsetClasses';
import { Loader } from '../../../../components/containers/Loader';
import { useKeypress } from '../../../../hooks/useKeypress';
import { OnboardingContext } from '../../../Onboarding/states/OnboardingProvider';
import { OnboardingStaticContent } from '../../../Onboarding/constants/content';

export type PostingPlanCloneCalendarViewProps = {
  timeFormat: TimeFormatSetting;
  exitHandler: CallableFunction;
  timeslot: GetTimeslots_workspace_timeslots;
  cloneTimeslotHandler: (newTimeslot: GetTimeslots_workspace_timeslots) => void;
};

const convertMinutesTo24HourTime = (timeInMinutes: number, hour: number): string => {
  // Create a base date starting at the specified hour with 0 minutes and seconds
  const baseTime = new Date();
  baseTime.setHours(hour, 0, 0, 0); // Set to the specified hour with zero minutes and seconds

  // Add the minutes within that hour
  const time = addMinutes(baseTime, timeInMinutes);

  // Format to 24-hour format
  return format(time, 'HH:mm');
};

export const PostingPlanCloneCalendarView = ({
  timeFormat,
  timeslot,
  cloneTimeslotHandler,
  exitHandler
}: PostingPlanCloneCalendarViewProps): JSX.Element => {
  const { timeslots, scrollBackPosition, setScrollBackPosition } = useContext(PostingPlanContext);
  const { selectedOrgSettings } = useSettings();
  const tableRef = useRef<HTMLTableElement | null>(null);
  const { setTableRef, cellTimeData } = useTableCellTimeTracking();
  const [shouldScroll, setShouldScroll] = useState<boolean>(true);
  const breakpoint = useBreakpoints();
  const [showMobileView, setShowMobileView] = useState(breakpoint === BreakpointsEnum.small);
  const { width } = useDimensions(tableRef);
  const [initialScrollDone, setInitialScrollDone] = useState(false);
  const { showOnboarding, state: onboardingState } = useContext(OnboardingContext);
  const { version } = onboardingState;
  const disableDelete = OnboardingStaticContent[`v${version}`].disableDelete.timeslots;

  // Exit clone mode if user presses escape key
  useKeypress('Escape', e => {
    if (e?.key === 'Escape') {
      exitHandler();
    }
  });

  const attachedCardRef = useRef<HTMLDivElement>(null);
  const { setRefs, setConditionHandler } = useCursorAttachment();

  const dayStartOfWeek =
    selectedOrgSettings?.startOfWeekFormat === StartOfWeekFormatSetting.SUNDAY
      ? StartOfWeekFormatSetting.SUNDAY
      : StartOfWeekFormatSetting.MONDAY;

  const renderCallback = useCallback((elem: HTMLTableElement | null) => {
    tableRef.current = elem;
  }, []);

  const onScroll: EventListener = useCallback(
    (e: Event) => {
      // Don't do anything if we are programmatically moving the scroll
      if (!initialScrollDone) return;

      const parent = e.currentTarget as HTMLDivElement;
      const scrollTop = parent.scrollTop;
      const scrollLeft = parent.scrollLeft;

      setScrollBackPosition({ left: scrollLeft, top: scrollTop });
    },
    [initialScrollDone, setScrollBackPosition]
  );

  // Excludes header and row header (first column) as valid regions
  const isValidCursorPosition = useCallback((event: Event | MouseEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement;

    const isInThead = target.closest('thead') !== null;
    const tbodyRow = target.closest('tbody tr') as HTMLTableRowElement | null;

    // Check if the event is in the first column of the row
    const isFirstColumn = tbodyRow && target.closest('td')?.isSameNode(tbodyRow.cells[0]);

    return !isInThead && !isFirstColumn;
  }, []);

  useEffect(() => {
    if (breakpoint === BreakpointsEnum.small) {
      if (!showMobileView) {
        setShowMobileView(true);
      }
      if (!shouldScroll) {
        setShouldScroll(true);
      }
    } else {
      if (showMobileView) {
        setShowMobileView(false);
      }
    }
  }, [breakpoint, shouldScroll, showMobileView]);

  useDidMount(() => {
    setRefs({
      elementRef: attachedCardRef,
      containerRef: tableRef
    });

    setConditionHandler(isValidCursorPosition);

    if (tableRef) {
      setTableRef(tableRef.current);
    }
  });

  const calendarClickHandler = (e: MouseEvent<HTMLDivElement>) => {
    // If not in a valid cell location, do not process click event
    if (!isValidCursorPosition(e)) return;
    // Only process if we are in an actual date cell
    if (cellTimeData?.cellIndex && e.currentTarget) {
      const day = PostingPlanHelpers.getDayFromColumn(cellTimeData.cellIndex, selectedOrgSettings?.startOfWeekFormat);
      const time = convertMinutesTo24HourTime(cellTimeData?.timeInMinutes || 0, cellTimeData?.rowIndex || 0);

      const newTimeslot = {
        ...timeslot,
        ...{
          day,
          time
        }
      };

      cloneTimeslotHandler(newTimeslot);
    }
  };

  const getSocialProfileImageFromTimeslot = (timeslotId: string, socialProfileId: string): string => {
    const flattenedTimeslots = PostingPlanHelpers.flattenTimeslotsCellData(timeslots);
    // The cloned timeslotId will be prepended with 'clone-' so that needs to be removed
    const rawTimeslotId = timeslotId.replace('clone-', '');
    const mappedTimeslot = flattenedTimeslots.find(t => t.id === rawTimeslotId);

    // Return an empty string if no timeslot is found
    if (!mappedTimeslot) return '';

    const socialProfile = mappedTimeslot.socialProfiles.find(s => s.id === socialProfileId);

    // Return the image URL if found, else an empty string
    return socialProfile ? socialProfile.image : '';
  };

  // Memoized width of the last <td> in the first <tr>
  const columnWidth = useMemo(() => {
    const tbody = tableRef.current?.querySelector('tbody');
    if (!tbody || !width) return null;

    const firstRow = tbody.querySelector('tr:first-child');
    if (!firstRow) return null;

    const lastCell = firstRow.querySelector('td:last-child');
    if (!lastCell) return null;

    return lastCell.getBoundingClientRect().width;
  }, [width]);

  // Apply calculated column width to attachedCardRef
  useLayoutEffect(() => {
    if (attachedCardRef.current && columnWidth) {
      attachedCardRef.current.style.width = `${columnWidth}px`;
    }
  }, [columnWidth]);

  useDidMount(() => {
    if (!initialScrollDone) {
      const parent = tableRef.current?.parentElement;

      if (!parent) return;

      parent.scrollTo({
        ...scrollBackPosition,
        behavior: 'smooth'
      });

      setInterval(() => {
        setInitialScrollDone(true);
      }, 500);
    }
  });

  const topNavOffsetContext = useContext(TopNavContext);
  const topNavClasses = topNavOffsetClassesGetter(topNavOffsetContext.state.visibility, 0);
  const singleColumnClasses = classNames(classes.CloneSingleColumn, topNavClasses.vheight, {});

  return (
    <div className={singleColumnClasses}>
      <div ref={tableRef} onClick={calendarClickHandler} className={classes.CalendarWrapper}>
        {!initialScrollDone && <Loader variation={'dark'} size={'large'} type={'overlay'} />}
        <Calendar
          onScroll={onScroll}
          fixedHeader={true}
          renderCallback={renderCallback}
          data={timeslots}
          headerNames={PostingPlanHelpers.getHeaderNames(dayStartOfWeek)}
          startDayOfWeek={dayStartOfWeek}
          cellTemplate={CloneCellTemplate}
          timeFormat={timeFormat}
          augmentedClasses={calendarClasses}
        />
      </div>
      <div ref={attachedCardRef} className={classes.AttachedTimeslotWrapper}>
        <Timeslot
          colour={timeslot.category.color}
          hovering={false}
          id={timeslot.id}
          isDragging={false}
          name={timeslot.category.name}
          onboardingContent={timeslot.onboardingContent && showOnboarding && disableDelete}
          socialProfiles={timeslot.socialProfiles.map(
            profile =>
              ({
                disabled: false,
                provider: profilesHelpers.mapOAuthFromTypename(profile.__typename),
                network: profilesHelpers.mapNetworkFromTypename(profile.__typename),
                image: getSocialProfileImageFromTimeslot(timeslot.id, profile.id),
                id: profile.id,
                __typename: profile.__typename,
                connected: true
              } as TimeslotSocialProfileType)
          )}
          timeFormat={selectedOrgSettings?.timeFormat ?? TimeFormatSetting.HOUR_24}
          categoryId={'1'}
          day={TimeslotDay.MONDAY}
          isListView={false}
          time={convertMinutesTo24HourTime(cellTimeData?.timeInMinutes || 0, cellTimeData?.rowIndex || 0)}
        />
      </div>
    </div>
  );
};
